import React from 'react';

import PropTypes from 'prop-types';

import useRequestError from 'components/hooks/useRequestError';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ErrorMessage from 'components/application/ErrorMessage';
import ModalBox from 'components/application/ModalBox';
import TextField from 'components/application/TextField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { useProjectRelocationModalContext } from 'components/projectsDashboard/contexts/ProjectRelocationModalContext';
import { pluralizeProjectsText } from 'components/projectsDashboard/helpers/index';

import DriveFileMove from '-!svg-react-loader?name=DriveFileMove!icons/drive-file-move.svg';
import InfoIcon from '-!svg-react-loader?name=InfoIcon!icons/info.svg';

export default function GroupIntoFolderModal({
  onClose,
  onMoveToExistingFolderClick,
  onSuccess,
  selectedProjectIds,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const {
    createFolderMutation,
    creationBlockingExistingFolder,
    currentFolder,
    handleCurrentFolderInputChange,
    handleDestinationFolderSelect,
    handleReset,
  } = useProjectRelocationModalContext();

  const [
    requestError,
    ,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const handleSubmit = () => {
    createFolderMutation.mutate(
      {
        folderParams: { ...currentFolder, project_ids: selectedProjectIds },
      },
      {
        onSuccess: (data) => {
          resetRequestError();
          handleReset();
          onSuccess({ folderId: data.data.id });
        },
        onError: (error) => {
          if (error.response.status === 422) {
            handleRequestError(error);
          } else {
            breadBoard.addInedibleToast({
              fullMessage: 'Could not create new folder',
            });
          }
        },
      },
    );
  };

  const ramsName = pluralizeProjectsText({
    count: selectedProjectIds.length,
    currentActor,
    includeCount: false,
    isCapitalized: false,
  });

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={() => {
              handleReset();
              onClose();
            }}
            size='md'
          >
            Cancel
          </TextButton>
          <FilledButton
            color='mint'
            disabled={
              currentFolder.name.length === 0 || createFolderMutation.isLoading
            }
            onClick={handleSubmit}
          >
            Create folder
          </FilledButton>
        </div>
      }
      isOpen={true}
      mode='letterbox'
      onClose={() => {
        handleReset();
        onClose();
      }}
    >
      <>
        <div className='tw-mx-6 tw-mt-6'>
          <h2 className='modalbox-header__title truncated-text-container tw-text-xl tw-font-semibold tw-text-grey-900'>
            Group{' '}
            {pluralizeProjectsText({
              count: selectedProjectIds.length,
              currentActor,
              isCapitalized: false,
            })}{' '}
            into new folder
          </h2>
        </div>
        <div className='modalbox-body'>
          {Object.keys(requestError.validationErrors).length === 0 && (
            <div className='tw-mb-6 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
              <div className='tw-mr-3 tw-h-5'>
                <InfoIcon
                  className='[&_path]:tw-fill-cyan-800'
                  height={20}
                  width={20}
                />
              </div>
              <p className='tw-mb-0'>
                Selected {ramsName} will be{' '}
                <span className='tw-font-semibold'>
                  moved from their current folder
                </span>
              </p>
            </div>
          )}
          {creationBlockingExistingFolder ?
            <div className='tw-mb-6 tw-rounded-lg tw-border-0 tw-bg-red-025 tw-p-4 tw-text-red-800'>
              <p className='tw-mb-2 tw-text-m tw-font-semibold'>
                A folder with this name already exists in this division.
              </p>
              <p className='tw-mb-2'>
                Would you like to move the selected {ramsName} to this folder
                instead?
              </p>
              <button
                className='tw-flex tw-h-8 tw-max-w-full tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-300 tw-bg-white tw-pl-2.5 tw-pr-4 tw-font-medium tw-text-grey-900'
                onClick={() => {
                  handleDestinationFolderSelect(
                    creationBlockingExistingFolder.id,
                  );

                  onMoveToExistingFolderClick();
                }}
                type='button'
              >
                <DriveFileMove
                  className='tw-flex-none [&_path]:tw-fill-grey-900'
                  height={20}
                  width={20}
                />
                <p className='tw-mb-0 tw-truncate'>
                  Move to {creationBlockingExistingFolder.attributes.name}
                </p>
              </button>
            </div>
          : <ErrorMessage
              isFallback={false}
              modifiers='tw-mb-3'
              validationErrors={requestError.validationErrors}
            />
          }
          <TextField
            autoFocus={true}
            fieldError={requestError.validationErrors.name}
            inputClassName='tw-px-3 tw-py-[9px]'
            isRequired={true}
            label='Folder name'
            name='name'
            onChange={handleCurrentFolderInputChange}
            placeholder='Enter unique name for folder'
            removeErrorStyling={removeErrorStyling}
            value={currentFolder.name}
          />
        </div>
      </>
    </ModalBox>
  );
}

GroupIntoFolderModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onMoveToExistingFolderClick: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  selectedProjectIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};
